import { useTranslation } from 'react-i18next'
import type { ColumnDefinition } from '@/components'
import { Table } from '@/components'
import {
  AccessTokenInfoDtoType,
  type DocumentDto,
} from '@cencert/mob-signature'
import { getAddDocumentDisabledReason } from '@/utils/fileValidation'
import { getAccessTokenType } from '@/services'

interface DocumentTableProps {
  documents: DocumentDto[]
  onAddDocument?: () => void
  onRemoveDocument?: (id: string) => void
  className?: string
  readonly?: boolean
  loading?: boolean
}

export function DocumentTable({
  documents,
  onAddDocument,
  onRemoveDocument,
  className = '',
  readonly = false,
  loading = false,
}: DocumentTableProps) {
  const { t } = useTranslation()
  const isHashProcess = getAccessTokenType() === AccessTokenInfoDtoType.HASH

  const disabledReason = getAddDocumentDisabledReason(documents, t)

  const getDocumentNameOnly = (fullName: string) => {
    const lastSlash = fullName.lastIndexOf('/')
    const lastBackslash = fullName.lastIndexOf('\\')
    const lastSeparator = Math.max(lastSlash, lastBackslash)
    return lastSeparator >= 0 ? fullName.substring(lastSeparator + 1) : fullName
  }

  const getSignatureTypeDisplayName = (
    signatureType: string,
    code?: string
  ) => {
    if (!code && !isHashProcess) {
      return t('documentAdd.signatureTypes.freePades')
    }

    switch (signatureType) {
      case 'freeSignature':
        return t('documentAdd.signatureTypes.freePades')
      case 'PAdES':
        return t('documentAdd.signatureTypes.PAdES')
      case 'XAdES':
        return t('documentAdd.signatureTypes.XAdES')
      case 'XAdES_DETACHED':
        return t('documentAdd.signatureTypes.XAdES_DETACHED')
      case 'CAdES':
        return t('documentAdd.signatureTypes.CAdES')
      case 'CAdES_DETACHED':
        return t('documentAdd.signatureTypes.CAdES_DETACHED')
      default:
        return signatureType
    }
  }

  const columns: ColumnDefinition<DocumentDto>[] = [
    {
      label: t('documentAdd.table.documentName'),
      accessor: 'name',
      width: '40%',
      render: (name?: string) => getDocumentNameOnly(name ?? ''),
    },
    {
      label: t('documentAdd.table.signatureType'),
      accessor: 'documentSignFormat',
      render: (signatureType?: string, row?: DocumentDto) =>
        getSignatureTypeDisplayName(signatureType ?? '', row?.code),
    },
  ]

  return (
    <Table
      columns={columns}
      data={documents}
      onRowAction={readonly ? undefined : onRemoveDocument}
      headerAction={
        (readonly ?? !onAddDocument)
          ? undefined
          : {
              label: t('documentAdd.table.add'),
              onClick: onAddDocument,
              disabled: !!disabledReason,
              ...(disabledReason ? { disabledReason } : {}),
            }
      }
      emptyStateMessage={{
        title: t('documentAdd.table.noDocuments'),
        subtitle: t('documentAdd.table.addFirstDocument'),
      }}
      readonly={readonly}
      className={className}
      loading={loading}
    />
  )
}
